/**
 * Server-side template detection.
 * Reads configuration from Cloudflare Worker–injected window.__TEMPLATE_CONFIG__
 * (shape: templateName, province, detectionMethod, etc.). Cookie fallback uses
 * Worker cookie names: user_country, template_type, user_language, user_province.
 */

/** ISO2 en mayúsculas o "UNKNOWN". Evita que `country === "AR"` falle si el Worker manda "ar". */
export function normalizeWorkerCountry(raw: unknown): string {
  if (raw === undefined || raw === null) return "UNKNOWN";
  const s = String(raw).trim();
  if (!s) return "UNKNOWN";
  const up = s.toUpperCase();
  if (up === "UNKNOWN") return "UNKNOWN";
  if (/^[A-Z]{2}$/.test(up)) return up;
  const two = up.slice(0, 2);
  if (/^[A-Z]{2}$/.test(two)) return two;
  return "UNKNOWN";
}

function normalizeWorkerLanguage(raw: unknown, countryCode: string): string {
  const s = String(raw ?? "").trim().replace(/_/g, "-");
  if (!s) return countryCode === "AR" ? "es-AR" : "en-US";
  return s;
}

export interface ServerTemplateConfig {
  country: string;
  templateName: string;
  bioLayout: string | null;
  language: string;
  province: string | null;
  /** Método de detección de provincia AR en el Worker (p. ej. iso_region_code, default_fallback). */
  provinceDetectionMethod?: string | null;
  cfCountry: string;
  timezone: string;
  city: string;
  /**
   * Canonical city key after worker city_catalog alias (e.g. "new york" for
   * CF "New York City"). Prefer over `city` for template poll / cascade match.
   */
  cityKey?: string | null;
  /**
   * Dual CTA surface from worker pathname (intl | serMiembro). Front must not
   * map path → surface locally.
   */
  dualSurface?: string | null;
  detectionMethod: string;
  /**
   * Worker flag for "Best Friend Instagram" (Close Friends) CTA in AR provinces.
   * Kept as `whatsappBroadcast` to preserve Cloudflare Worker contract — the
   * frontend renames it to `bestfriendInstagram` on the way out (useTemplateSelection).
   */
  whatsappBroadcast?: boolean;
  /** Wishlist CTA (Worker / admin `cta_type=wishlist`; en AR según provincia o DB) */
  showWishlist?: boolean;
  /** Hide all CTA buttons for this province (AR_NO_CTA_PROVINCES in Worker) */
  hideCta?: boolean;
  /** CTA comunidad → /invitacion-premium (Worker / DB) */
  showCommunity?: boolean;
  /** CTA "Unite a Mi Comunidad" → /whatsapp-broadcast (Worker / admin cta_type=whatsapp) */
  showWhatsappCommunity?: boolean;
  /** CTA "Mejores Amigos Instagram" → /instagram-mejores-amigos (Worker / admin cta_type=instagram) */
  showInstagram?: boolean;
  /** CTA cumpleaños (Worker) */
  showBirthday?: boolean;
  /** Dual CTA: botón paid + botón free simultáneos (cta_type="dual" en wishlist-config) */
  showDual?: boolean;
  /** FORCE_BASE_TEMPLATE kill switch activo en el Worker */
  killSwitchActive?: boolean;
  /** GLOBAL_HIDE_HERO_CTA — suprime CTAs hero y templates dual en home */
  globalHideHeroCta?: boolean;
  /** Fuente del template: privacy_lock | city_override | state_override | country_override | worker_default */
  templateSource?: string;
  /** Redirige BioTemplatePremiumSimple al menú VIP (cliente SPA) */
  redirectMenuVipReels?: boolean;
  /** True si la IP parece pertenecer a un VPN/datacenter/cloud (cf.asOrganization) */
  vpnDetected?: boolean;
  /** Nombre de la organización ASN detectada por Cloudflare */
  asOrganization?: string;
  /**
   * Contenido del CTA hero resuelto por el Worker per-geo (admin DB →
   * defaults por cta_type). Si el visitor cae en hideCta=true, vienen vacíos.
   * Coherente con los inputs del admin en /admin/wishlist (label/url/emoji
   * opcionales per entry city_ar / province_ar / country).
   */
  ctaType?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  ctaEmoji?: string;
  /**
   * Variante A/B activa para DualCTAVideoLanding, resuelta por el Worker
   * (catálogo `dual_cta_variants` + asignaciones `dual_cta_country_assignment`
   * + sticky bucket cookie). Solo presente cuando templateName ===
   * "DualCTAVideoLanding" y hay assignment para este geo.
   */
  dualVariant?: DualVariantConfig;
  /** Sticky bucket (cookie `dual_ab_bucket`, UUID v4) — propagado a PostHog/Stripe metadata. */
  abBucket?: string;
  /**
   * Fuente de resolución del CTA hero (mismo valor que header X-Cta-Source):
   * 'kv-hit', 'kv-miss-vercel', 'kv-miss-vercel-fail', 'db-vercel',
   * 'db-vercel-fail', 'edge'. Usado como super property en PostHog para
   * monitorear hit-rate de KV vs Railway fallback en producción.
   */
  ctaResolutionSource?: string;
  /**
   * Estado de EE.UU. detectado por el Worker (cf.regionCode para country=US).
   * Formato: ISO 3166-2 abreviado en mayúsculas (e.g. "CA", "FL", "NY").
   * Presente solo cuando country==="US" y la detección fue exitosa.
   * Usado para cascada state_us en dual variant resolution y analytics.
   */
  usState?: string | null;
  /** Fuente de la detección del estado US ("cf_regionCode" | "manual_override" | null). */
  usStateDetectionMethod?: string | null;
  /**
   * Barrio AR resuelto por el Worker (added 2026-07).
   * El Worker usa coords (cookies user_lat/lng > cf.latitude/longitude) → bbox
   * lookup contra lib/argentinaNeighborhoods.js. IP→barrio NO es 100% confiable;
   * usar para soft CTAs/analytics, nunca para gating.
   */
  neighborhood?: string | null;
  /** City derivada del polygon hit de barrio — puede diferir de CF.city en edge cases. */
  neighborhoodCity?: string | null;
  /** Fuente del dato geo más específico. */
  geoSource?:
    | "cf_only"
    | "iplocate"
    | "cache"
    | "neighborhood_polygon"
    | "device_geolocation"
    | "manual_override";
  /**
   * Conflict geo detectado: regionCode/region asignó una provincia AR pero
   * cf.city sugiere OTRA (homónimo, edge case tipo Bella Vista BA vs Corrientes).
   * Si presente, el frontend dispara un evento `geo_city_province_conflict`
   * una sola vez en mount para auditoría.
   */
  geoConflict?: {
    provFromRegion: string;
    provFromCity: string;
    cfCity: string;
  };
  /**
   * CTA hero global definido por admin en site-settings (heroCta_enabled +
   * heroCta_label + heroCta_url + heroCta_emoji + heroCta_type). El Worker SSR
   * lo resuelve aquí (incluye routing membership Stripe/MP por país via
   * MEMBERSHIP_LATAM). El frontend lo lee y lo pasa como `adminCta` prop a
   * BaseTemplate — prioridad sobre el workerCta per-geo.
   *
   * Sin este, los settings de heroCta_* en /admin/site-settings quedaban
   * invisibles al visitor (bug pre-2026-06).
   */
  heroCta?: {
    enabled: boolean;
    label: string;
    url: string;
    emoji?: string;
    type: "link" | "membership";
  };
}

export interface DualVariantConfig {
  id: string;
  /** Nombre legible del admin para identificar la variante en tracking. */
  displayName?: string;
  muxPlaybackId?: string;
  vipLabel?: string;
  vipSubtitle?: string;
  freeLabel?: string;
  priceUsd?: number;
  priceArs?: number;
  /** Stripe Price ID propio (pisa membership_products). Format: price_xxx */
  stripePriceId?: string;
  /**
   * Promotion Code propio de la variante (promo_*). Descuento per-país aplicado
   * a su propio price (cupón sin restricción de producto). Prevalece sobre el
   * promo del contrato en resolveVipStripeCheckoutFromFallback.
   */
  stripePromotionCodeId?: string;
  /**
   * false = no aplicar cupón (toggle admin). Ausente/true = comportamiento legacy.
   */
  stripePromoEnabled?: boolean;
  /** Match con mercadopago_plans.variant_label */
  mpVariantLabel?: string;
  /** Override per-variant del free CTA URL. Sin set → cae al global. */
  freeTelegramUrl?: string;
  /** Override per-variant del toggle dual_cta_free. Sin set → cae al global. */
  freeEnabled?: boolean;
  /** Scope que ganó la cascada ('state_us' | 'country' | 'DEFAULT') */
  assignmentScope?: string;
  /** Geo code que ganó ('AR', 'US', 'FL', 'DEFAULT', etc.) */
  assignmentGeo?: string;
  /** Capa de presentación por variante (theming + secciones) — blob opaco JSONB. */
  presentation?: DualVariantPresentation;
}

/**
 * Config de presentación por variante (migración 064). Todo opcional; ausente =
 * layout/tema actual. Diferencia visualmente cada mercado sin forkear el template.
 */
export interface DualVariantPresentation {
  theme?: { accent?: string; bg?: string; font?: string };
  sections?: {
    trustBadges?: boolean;
    socialProof?: boolean;
    testimonials?: boolean;
    paymentIcons?: boolean;
    countdown?: boolean;
  };
  trustSignals?: string[];
  testimonials?: Array<{ quote: string; author?: string }>;
  paymentMethods?: string[];
  currencyDisplay?: string;
}

function normalizeDualVariant(raw: unknown): DualVariantConfig | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const v = raw as Record<string, unknown>;
  const id = typeof v.id === "string" ? v.id.trim() : "";
  if (!id) return undefined;
  const str = (x: unknown): string | undefined =>
    typeof x === "string" && x.trim() ? x.trim() : undefined;
  const num = (x: unknown): number | undefined =>
    typeof x === "number" && Number.isFinite(x) ? x : undefined;
  return {
    id,
    displayName: str(v.displayName),
    muxPlaybackId: str(v.muxPlaybackId),
    vipLabel: str(v.vipLabel),
    vipSubtitle: str(v.vipSubtitle),
    freeLabel: str(v.freeLabel),
    priceUsd: num(v.priceUsd),
    priceArs: num(v.priceArs),
    stripePriceId: str(v.stripePriceId),
    stripePromotionCodeId: str(v.stripePromotionCodeId),
    stripePromoEnabled:
      typeof v.stripePromoEnabled === "boolean"
        ? v.stripePromoEnabled
        : typeof v.stripe_promo_enabled === "boolean"
          ? v.stripe_promo_enabled
          : undefined,
    mpVariantLabel: str(v.mpVariantLabel),
    freeTelegramUrl: str(v.freeTelegramUrl),
    freeEnabled: typeof v.freeEnabled === "boolean" ? v.freeEnabled : undefined,
    assignmentScope: str(v.assignmentScope),
    assignmentGeo: str(v.assignmentGeo),
    presentation:
      v.presentation && typeof v.presentation === "object" && !Array.isArray(v.presentation)
        ? (v.presentation as DualVariantPresentation)
        : undefined,
  };
}

function normalizeGeoConflict(
  raw: unknown,
): ServerTemplateConfig["geoConflict"] | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const v = raw as Record<string, unknown>;
  const provFromRegion = typeof v.provFromRegion === "string" ? v.provFromRegion : "";
  const provFromCity = typeof v.provFromCity === "string" ? v.provFromCity : "";
  const cfCity = typeof v.cfCity === "string" ? v.cfCity : "";
  if (!provFromRegion || !provFromCity || !cfCity) return undefined;
  return { provFromRegion, provFromCity, cfCity };
}

/**
 * Parsea el heroCta global que el Worker SSR resolvió desde site_settings.
 * Worker.js:5937-5972 lo emite solo cuando heroCta_enabled=true + label +
 * resolvedUrl (routing membership Stripe/MP por país). Sin esto, el frontend
 * recibe undefined y BaseTemplate cae al workerCta per-geo (cta_type) o null.
 */
function normalizeHeroCta(
  raw: unknown,
): ServerTemplateConfig["heroCta"] | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const v = raw as Record<string, unknown>;
  if (v.enabled !== true) return undefined;
  const label = typeof v.label === "string" ? v.label.trim() : "";
  const url = typeof v.url === "string" ? v.url.trim() : "";
  if (!label || !url) return undefined;
  const type = v.type === "membership" ? "membership" : "link";
  const emoji = typeof v.emoji === "string" && v.emoji.trim() ? v.emoji.trim() : undefined;
  return { enabled: true, label, url, emoji, type };
}

/**
 * Get template configuration from server-injected window object
 * The Cloudflare Worker injects this before the page loads
 */
export function getServerTemplateConfig(): ServerTemplateConfig | null {
  if (typeof window === "undefined") return null;

  // Primary source: injected by Cloudflare Worker
  const serverConfig = (
    window as unknown as Window & {
      __TEMPLATE_CONFIG__?: Partial<ServerTemplateConfig> & Record<string, unknown>;
    }
  ).__TEMPLATE_CONFIG__;

  if (serverConfig) {
    const country = normalizeWorkerCountry(serverConfig.country);
    return {
      country,
      templateName: serverConfig.templateName || "BaseTemplate",
      bioLayout: serverConfig.bioLayout || null,
      language: normalizeWorkerLanguage(serverConfig.language, country),
      province: serverConfig.province || null,
      provinceDetectionMethod:
        serverConfig.provinceDetectionMethod != null
          ? String(serverConfig.provinceDetectionMethod)
          : null,
      cfCountry: normalizeWorkerCountry(
        serverConfig.cfCountry ?? serverConfig.country,
      ),
      timezone: serverConfig.timezone || "",
      city: serverConfig.city || "",
      cityKey:
        typeof serverConfig.cityKey === "string" && serverConfig.cityKey.trim()
          ? serverConfig.cityKey.trim().toLowerCase()
          : null,
      dualSurface:
        typeof serverConfig.dualSurface === "string" &&
        (serverConfig.dualSurface === "intl" ||
          serverConfig.dualSurface === "serMiembro")
          ? serverConfig.dualSurface
          : null,
      detectionMethod: serverConfig.detectionMethod || "cloudflare_geo",
      whatsappBroadcast: !!serverConfig.whatsappBroadcast,
      showWishlist: !!serverConfig.showWishlist,
      hideCta: !!serverConfig.hideCta,
      showCommunity: !!serverConfig.showCommunity,
      showWhatsappCommunity: !!serverConfig.showWhatsappCommunity,
      showInstagram: !!serverConfig.showInstagram,
      showBirthday: !!serverConfig.showBirthday,
      showDual: !!serverConfig.showDual,
      killSwitchActive: serverConfig.killSwitchActive === true,
      globalHideHeroCta: serverConfig.globalHideHeroCta === true,
      templateSource:
        typeof serverConfig.templateSource === "string"
          ? serverConfig.templateSource
          : undefined,
      redirectMenuVipReels:
        serverConfig.redirectMenuVipReels === true ? true : undefined,
      vpnDetected: serverConfig.vpnDetected === true,
      asOrganization: typeof serverConfig.asOrganization === "string" ? serverConfig.asOrganization : undefined,
      ctaType:  typeof serverConfig.ctaType  === "string" ? serverConfig.ctaType  : undefined,
      ctaLabel: typeof serverConfig.ctaLabel === "string" ? serverConfig.ctaLabel : undefined,
      ctaUrl:   typeof serverConfig.ctaUrl   === "string" ? serverConfig.ctaUrl   : undefined,
      ctaEmoji: typeof serverConfig.ctaEmoji === "string" ? serverConfig.ctaEmoji : undefined,
      usState:
        typeof serverConfig.usState === "string" && serverConfig.usState.trim()
          ? serverConfig.usState.trim().toUpperCase().slice(0, 2)
          : null,
      usStateDetectionMethod:
        typeof serverConfig.usStateDetectionMethod === "string" && serverConfig.usStateDetectionMethod.trim()
          ? serverConfig.usStateDetectionMethod.trim()
          : null,
      neighborhood:
        typeof serverConfig.neighborhood === "string" && serverConfig.neighborhood.trim()
          ? serverConfig.neighborhood.trim()
          : null,
      neighborhoodCity:
        typeof serverConfig.neighborhoodCity === "string" && serverConfig.neighborhoodCity.trim()
          ? serverConfig.neighborhoodCity.trim()
          : null,
      geoSource:
        typeof serverConfig.geoSource === "string"
          ? (serverConfig.geoSource as ServerTemplateConfig["geoSource"])
          : undefined,
      dualVariant: normalizeDualVariant(serverConfig.dualVariant),
      abBucket: typeof serverConfig.abBucket === "string" ? serverConfig.abBucket : undefined,
      ctaResolutionSource:
        typeof serverConfig.ctaResolutionSource === "string"
          ? serverConfig.ctaResolutionSource
          : undefined,
      geoConflict: normalizeGeoConflict(serverConfig.geoConflict),
      heroCta: normalizeHeroCta(serverConfig.heroCta),
    };
  }

  // Fallback: try to read from cookies (set by Worker)
  const cookies = document.cookie.split(";").reduce(
    (acc, cookie) => {
      const [key, value] = cookie.trim().split("=");
      acc[key] = value;
      return acc;
    },
    {} as Record<string, string>,
  );

  if (cookies.user_country) {
    let countryNorm = "";
    try {
      countryNorm = decodeURIComponent(cookies.user_country)
        .trim()
        .toUpperCase()
        .slice(0, 2);
    } catch {
      countryNorm = cookies.user_country.trim().toUpperCase().slice(0, 2);
    }
    // WhatsApp broadcast: only for AR with qualifying provinces
    const arProvinces = ["CABA", "Buenos Aires", "Santa Fe", "Entre Ríos", "Río Negro", "San Luis", "La Pampa"];
    const province = cookies.user_province || null;
    const whatsappBroadcast =
      countryNorm === "AR" && province && arProvinces.includes(province);

    const rawLang = cookies.user_language?.replace(/_/g, "-") || "";
    const langFromCookie =
      countryNorm === "AR"
        ? rawLang.toLowerCase().startsWith("es")
          ? rawLang
          : "es-AR"
        : rawLang || "en-US";

    return {
      country: countryNorm || normalizeWorkerCountry(cookies.user_country),
      templateName: cookies.template_type || "BaseTemplate",
      bioLayout: null,
      language: normalizeWorkerLanguage(
        langFromCookie,
        countryNorm || "UNKNOWN",
      ),
      province,
      provinceDetectionMethod: null,
      cfCountry: normalizeWorkerCountry(cookies.user_country),
      timezone: "",
      city: "",
      detectionMethod: "cookies_fallback",
      whatsappBroadcast,
      showWishlist: false, // cookies don't carry showWishlist; Worker re-evaluates on each request
      hideCta: false,
      showCommunity: false,
      showWhatsappCommunity: false,
      showInstagram: false,
      showBirthday: false,
      showDual: false,
      redirectMenuVipReels: undefined,
    };
  }

  return null;
}

/** Decode Worker geo strings (e.g. URL-encoded city names) for analytics display. */
function decodeGeoSegment(raw: string | null | undefined): string | undefined {
  if (raw == null || !String(raw).trim()) return undefined;
  const s = String(raw).trim();
  try {
    return decodeURIComponent(s.replace(/\+/g, " "));
  } catch {
    return s;
  }
}

/** Client-side overlay after async /api/geolocate?enrich=1 (Worker IPLocate is waitUntil on cache miss). */
let clientGeoEnrichmentOverlay: Record<string, string | undefined> | null = null;

export function setClientGeoEnrichmentOverlay(
  props: Record<string, string | undefined>,
): void {
  clientGeoEnrichmentOverlay = props;
}

export function clearClientGeoEnrichmentOverlay(): void {
  clientGeoEnrichmentOverlay = null;
}

/**
 * Worker-derived geo fields for PostHog / GA (complements IP-based $geoip_* on the person).
 * Use on high-value CTAs so alerts match country · province · city without encoded %20 artifacts.
 */
export function getWorkerGeoAnalyticsProps(): Record<string, string | undefined> {
  const workerProps = buildWorkerGeoProps();
  if (clientGeoEnrichmentOverlay) {
    // VERDAD ÚNICA DE LOCATION: el Worker manda. El enrichment del cliente
    // (/api/geolocate) solo COMPLETA campos que el Worker no resolvió
    // (VPN/ASN/hosting cuando IPLocate no corrió en el edge) — nunca pisa
    // country/province/city ya resueltos. Antes el overlay REEMPLAZABA todo
    // y los eventos salían con la provincia del fallback (p. ej. "BA" de
    // ipinfo) en vez de la del Worker ("CABA").
    const merged: Record<string, string | undefined> = { ...clientGeoEnrichmentOverlay };
    for (const [k, v] of Object.entries(workerProps)) {
      if (v !== undefined && v !== "") merged[k] = v;
    }
    return merged;
  }
  return workerProps;
}

function buildWorkerGeoProps(): Record<string, string | undefined> {
  const cfg = getServerTemplateConfig();
  if (!cfg) return {};
  const city = decodeGeoSegment(cfg.city);
  const province = cfg.province?.trim() || undefined;
  const country =
    cfg.country && cfg.country !== "UNKNOWN" ? cfg.country : undefined;
  const cf =
    cfg.cfCountry && cfg.cfCountry !== "UNKNOWN" ? cfg.cfCountry : undefined;
  const parts = [country, province, city].filter(
    (x): x is string => typeof x === "string" && x.length > 0,
  );
  const usState = cfg.usState?.trim() || undefined;
  const neighborhood = cfg.neighborhood?.trim() || undefined;
  return {
    worker_country: country,
    worker_cf_country: cf,
    worker_province: province,
    worker_us_state: usState,
    worker_city: city,
    worker_neighborhood: neighborhood,
    worker_geo_source: cfg.geoSource,
    worker_vpn_detected: cfg.vpnDetected ? "true" : undefined,
    worker_asn_organization: cfg.asOrganization,
    worker_timezone: cfg.timezone || undefined,
    worker_language: cfg.language || undefined,
    worker_template: cfg.templateName || undefined,
    // Fuente del template servido — permite medir en PostHog el share de
    // privacy_lock (VPN/bot → BaseTemplate) y el alcance real de los
    // overrides city/state/country vs worker_default.
    worker_template_source: cfg.templateSource || undefined,
    worker_detection_method: cfg.detectionMethod || undefined,
    worker_us_state_detection_method: cfg.usStateDetectionMethod || undefined,
    geo_summary: parts.length ? parts.join(" · ") : undefined,
    // Hit-rate del CTA resolver: kv-hit (warm), kv-miss-vercel (cold + Railway OK),
    // kv-miss-vercel-fail, db-vercel, db-vercel-fail, edge (sin DB).
    cta_resolution_source: cfg.ctaResolutionSource || undefined,
    // HeroCTA real que el visitor está viendo — admin filtra PostHog por
    // hero_cta_type para A/B insights ("comunidad convierte mejor que wishlist
    // en Tigre"), y por hero_cta_label para detectar mismatch entre config
    // admin y lo que efectivamente se renderizó.
    hero_cta_type: cfg.ctaType || undefined,
    hero_cta_label: cfg.ctaLabel ? cfg.ctaLabel.slice(0, 80) : undefined,
  };
}

/**
 * Si el worker detectó un conflict geo (regionCode vs cityMap),
 * devuelve las props para capturar el evento. Caller (App o
 * registerSuperPropertiesPostHog) lo dispara una vez en mount.
 */
export function getGeoConflictEventProps():
  | { event: "geo_city_province_conflict"; props: Record<string, string> }
  | null {
  const cfg = getServerTemplateConfig();
  if (!cfg || !cfg.geoConflict) return null;
  return {
    event: "geo_city_province_conflict",
    props: {
      prov_from_region: cfg.geoConflict.provFromRegion,
      prov_from_city: cfg.geoConflict.provFromCity,
      cf_city: cfg.geoConflict.cfCity,
      resolved_province: cfg.province || "",
      worker_country: cfg.country || "",
    },
  };
}
